import React from 'react';
import { UpdateVisibility } from 'types/content/update';
import { CommunityUpdate } from 'types/content';
import SelectCompany from './SelectCompany';
import { getOrUndefined } from 'util/resource';
import useNotify from 'hooks/useNotify';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { TinyCommunity } from 'types/company/community';
import { userDashboardKey } from 'pages/Dashboard/useUserDashboardData';
import { invalidate } from 'hooks/useSWR';
import UpdateForm from '../UpdateForm';
import useLazyResource from 'util/resource/useLazyResource';
import { communityUpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import Dialog, { Title } from 'ui/views/dialogs/Dialog';
import { CompanyUpdateFormValues } from 'domain/Updates/company/types';
import useCommunityRole from 'hooks/useCommunityRole';
import { PostToCommunity, PostAs } from 'domain/Updates/shared/types';
import { reloadAllUpdates } from '../useCommunityUpdates';
import { useCommunitySummary } from 'apis/CompanyAPI/communities/useCommunitySummary';
import Resource from 'util/resource/Resource';
import { usePostAs } from './usePostAs';
import { EmbeddedUpdate, PostBlot } from 'ui/elements/form/RichTextEditor/components/UpdateBlot';
import { SINGLE_LINE_BREAK } from 'ui/elements/form/RichTextEditor';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  community: TinyCommunity;
  onUpdatePosted?: (update: CommunityUpdate) => void;
  postTo?: PostToCommunity;
  embeddedUpdate?: EmbeddedUpdate;
  successMessage?: string;
  initialPostingAs?: PostAs;
}

export default function PostUpdateDialog({
  isOpen,
  onClose,
  community,
  onUpdatePosted,
  postTo,
  embeddedUpdate,
  successMessage,
  initialPostingAs,
}: Props) {
  const notify = useNotify();

  const { resource: userResource } = useSelfUserProfile();
  const user = getOrUndefined(userResource);

  const { resource: communitySummary } = useCommunitySummary(community.slug);

  const { myCompanyMemberships: memberships, hasScope } = useCommunityRole(community.slug);
  const myCompanyMemberships = memberships.filter(m => m.visibility === 'Visible').map(m => m.company);

  const { postingAs, setPostingAs, author } = usePostAs(community, postTo, initialPostingAs);

  const handleClose = () => {
    onClose();
    setPostingAs(undefined);
  };

  const [createUpdate, isSaving] = useLazyResource(
    (dto: CompanyUpdateFormValues & { visibility: UpdateVisibility }) => {
      if (!postingAs || (postingAs.type === 'company' && !postingAs.company)) {
        throw new Error('Posting as company but no company is selected');
      }
      return communityUpdateAPI(community.id).create({
        ...dto,
        postedBy:
          postingAs.type === 'company'
            ? { type: 'company', company: postingAs.company, companyId: postingAs.company.id }
            : postingAs.type === 'user'
              ? { type: 'user', user: postingAs.user, cwUserId: postingAs.user.cwUserId }
              : { type: 'community' },
      });
    },
    {
      onSuccess: update => {
        handleClose();
        reloadAllUpdates(community.id);
        onUpdatePosted?.(update);
        invalidate(userDashboardKey);
        notify('success', successMessage ?? 'Update posted!');
      },
      onFailure: () => {
        notify('error', `Something went wrong when trying to post update`);
      },
    },
  );

  if (isOpen && postingAs) {
    return (
      <Resource resource={communitySummary}>
        {communitySummary => (
          <UpdateForm
            author={author}
            selectedVisibility={postTo?.visibility ?? 'CommunityMembers'}
            onSave={(dto, visibility) => createUpdate({ ...dto, visibility })}
            isSaving={isSaving}
            onClose={handleClose}
            community={community}
            postedBy={postingAs}
            communityConfiguration={communitySummary.configuration}
            contentDraft={
              embeddedUpdate
                ? SINGLE_LINE_BREAK + PostBlot.create(embeddedUpdate).outerHTML + SINGLE_LINE_BREAK
                : undefined
            }
            initialCursorPosition={embeddedUpdate ? 0 : undefined}
          />
        )}
      </Resource>
    );
  }

  function closeSelectCompany() {
    handleClose();
  }

  if (isOpen && !postingAs) {
    return (
      <Dialog open onClose={closeSelectCompany}>
        <Title onClose={closeSelectCompany}>Post community update</Title>
        <SelectCompany
          canPostAsCommunity={hasScope('Write', 'Update', 'Admin')}
          canPostAsUser={hasScope('Write', 'Update', 'Self')}
          community={community}
          setChosenCompany={setPostingAs}
          myCompanyMemberships={myCompanyMemberships}
          user={user}
        />
      </Dialog>
    );
  }

  return null;
}
